import * as React from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { useCanConfigureContractFamilies } from "@/hooks/use-contract-families-access";
import { cn } from "@/lib/utils";
import type { ContractSummaryStatus } from "@/types/supplier";
import { CancelAutomatedRunButton } from "@/views/contract-families/components/CancelAutomatedRunButton";
import { buildContractFamiliesPath } from "@/views/contract-families/navigation";
import { getContractSummaryFailureMessage } from "@/views/contract-families/pipeline/pipelineErrors";
import type { SupplierContractFolderContext } from "@/views/drive/useSupplierContractFolder";
import {
  CONTRACT_SUMMARY_ACTION_LABELS,
  contractSummaryAction,
  contractSummaryStatusBadge,
} from "@/views/suppliers/components/contract-summary-status";

interface Props {
  folderFseId: string;
  contractFolder: SupplierContractFolderContext;
}

export const CONTRACT_SUMMARY_STATUS_TEXT: Record<ContractSummaryStatus, string> = {
  none: "No contract summary has been created for this contract family yet.",
  current: "Contract summary is up to date, but has not been verified yet.",
  verified: "Contract summary is up to date and verified.",
  stale: "This contract family was amended, please recreate the contract summary.",
  in_progress: "Contract summary is being generated.",
  needs_review: "Contract summary needs your review.",
  failed: "Contract summary failed",
  empty: "All documents were removed from the folder. The contract summary is frozen.",
};

const TONES: Record<ContractSummaryStatus, string> = {
  none: "border-slate-200 bg-white text-gray-600",
  stale: "border-[#efd39f] bg-[#fdf0db] text-[#805515]",
  empty: "border-[#efd39f] bg-[#fdf0db] text-[#805515]",
  needs_review: "border-[#efc2bd] bg-[#fde8e6] text-[#9e3025]",
  failed: "border-[#efc2bd] bg-[#fde8e6] text-[#9e3025]",
  in_progress: "border-slate-200 bg-slate-100 text-slate-700",
  current: "border-slate-200 bg-white text-gray-600",
  verified: "border-[#b9dfcc] bg-[#e6f4ed] text-[#245f40]",
};

/** The status pill and message of a supplier folder's one-click contract summary. */
export const ContractSummaryStatusBanner: React.FC<Props> = ({ folderFseId, contractFolder }) => {
  const canConfigure = useCanConfigureContractFamilies();
  const { folder, status } = contractFolder;
  const badge = contractSummaryStatusBadge(status);
  const pill = (
    <Badge variant={badge.variant} aria-label={`Contract summary: ${badge.label}`}>
      {badge.label}
    </Badge>
  );

  const runId = folder?.contract_summary_run_id ?? null;
  // Only the person who started a run can open its workflow page.
  const runLink =
    runId && folder?.contract_summary_run_is_mine
      ? buildContractFamiliesPath(folderFseId, { pipelineRunId: runId })
      : null;
  const canCancel = Boolean(runId) && (Boolean(folder?.contract_summary_run_is_mine) || canConfigure);
  // The family row opens as a single snapshot, like the Supplier Centre "Contract Summary" button.
  const summaryLink = folder?.contract_summary_outcome_id
    ? `/reports/outcome/${encodeURIComponent(folder.contract_summary_outcome_id)}`
    : null;

  if (status === "failed") {
    return (
      <div
        role="alert"
        className={cn(
          "mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm",
          TONES.failed,
        )}
      >
        {pill}
        <div>
          <p>
            <span className="font-medium">{CONTRACT_SUMMARY_STATUS_TEXT.failed}:</span>{" "}
            {getContractSummaryFailureMessage(folder?.contract_summary_error_code)}
          </p>
          <p className="mt-1">Click {CONTRACT_SUMMARY_ACTION_LABELS[contractSummaryAction(folder)]} to try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm",
        TONES[status],
      )}
    >
      {pill}
      <span>{CONTRACT_SUMMARY_STATUS_TEXT[status]}</span>
      {runLink && status === "in_progress" ? (
        <Link to={runLink} className="font-medium underline">
          View progress
        </Link>
      ) : null}
      {runLink && status === "needs_review" ? (
        <Link to={runLink} className="font-medium underline">
          Review
        </Link>
      ) : null}
      {summaryLink && (status === "current" || status === "verified" || status === "stale" || status === "empty") ? (
        <Link to={summaryLink} className="font-medium underline">
          Open contract summary
        </Link>
      ) : null}
      {runId && canCancel && status === "in_progress" ? <CancelAutomatedRunButton runId={runId} /> : null}
    </div>
  );
};
